import type { CommandId, KeyInput, ToolId } from './tools/tool';

/** Single source of truth for shortcuts; the help panel is generated from it. */
export const KEYMAP: Record<string, ToolId | CommandId> = {
  // FigJam letters (backlog §8.2); shift+s is the former section key, kept as an alias.
  v: 'select', h: 'hand', t: 'text', s: 'sticky', r: 'shape', o: 'shape', l: 'arrow', i: 'image', x: 'connector', f: 'section', 'shift+s': 'section', 'shift+f': 'frame', c: 'comment',
  'mod+z': 'undo', 'mod+shift+z': 'redo', 'mod+d': 'duplicate', 'mod+a': 'selectAll', 'mod+g': 'group', 'mod+shift+g': 'ungroup',
  delete: 'delete', backspace: 'delete',
  ']': 'bringForward', '[': 'sendBackward', 'mod+]': 'bringToFront', 'mod+[': 'sendToBack', 'mod+shift+l': 'toggleLock',
  'alt+a': 'alignLeft', 'alt+h': 'alignHCenter', 'alt+d': 'alignRight',
  'alt+w': 'alignTop', 'alt+v': 'alignVCenter', 'alt+s': 'alignBottom',
  'alt+shift+h': 'distributeH', 'alt+shift+v': 'distributeV', 'alt+shift+t': 'arrange',
  'mod+alt+c': 'copyStyle', 'mod+alt+v': 'pasteStyle',
  'mod+k': 'palette',
  'alt+pagedown': 'nextPage', 'alt+pageup': 'prevPage',
  f2: 'renameSection',
  'shift+1': 'zoomFit', 'shift+2': 'zoomSelection', 'mod+0': 'zoomReset', 'mod+=': 'zoomIn', 'mod+-': 'zoomOut',
};

/** Handled by the browser's own copy/cut/paste events (KEYMAP must not swallow them), shown in the help. */
const CLIPBOARD_HELP: { combo: string; action: CommandId }[] = [
  { combo: 'mod+c', action: 'copy' },
  { combo: 'mod+x', action: 'cut' },
  { combo: 'mod+v', action: 'paste' },
];

export const SHORTCUT_HELP: { combo: string; action: ToolId | CommandId }[] = [
  ...Object.entries(KEYMAP).map(([combo, action]) => ({ combo, action })),
  ...CLIPBOARD_HELP,
];

const NAMED: Record<string, string> = { Delete: 'delete', Backspace: 'backspace', Escape: 'escape' };

export function comboOf(e: KeyInput): string {
  // Digits and, with Alt (macOS turns Alt+A into "å"), letters are read from the physical key.
  const key = e.code?.startsWith('Digit')
    ? e.code.slice(5)
    : e.alt && e.code?.startsWith('Key')
      ? e.code.slice(3).toLowerCase()
      : (NAMED[e.key] ?? e.key.toLowerCase());
  return [(e.meta || e.ctrl) && 'mod', e.alt && 'alt', e.shift && 'shift', key].filter(Boolean).join('+');
}

export function lookupShortcut(e: KeyInput): ToolId | CommandId | undefined {
  return KEYMAP[comboOf(e)];
}
